import { useEffect, useId, useRef, useState } from "react";
import { AlertCircle, Loader2, Lock, Paperclip, Trash2, Upload, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { uploadAttachment } from "@/api/attachments";
import { uploadComplaintAttachment } from "@/api/complaints";
import { useProposeResolution } from "@/hooks/useComplaints";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import type { ComplaintResolutionType } from "@/api/types";
import { FINANCIAL_RESOLUTION_TYPES, RESOLUTION_TYPE_LABEL } from "./constants";

const RESOLUTION_TYPES: ComplaintResolutionType[] = [
  "refund_full",
  "refund_partial",
  "product_replacement",
  "credit_note",
  "discount_next_order",
  "service_apology",
  "explanation_only",
  "other",
];

interface EvidenceItem {
  attachmentId: string;
  fileName: string;
}

export function ResolveDrawer({
  complaintId,
  open,
  onClose,
  onDone,
}: {
  complaintId: string;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();
  const fileRef = useRef<HTMLInputElement>(null);

  const [type, setType] = useState<ComplaintResolutionType>("service_apology");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mut = useProposeResolution(complaintId);

  useEffect(() => {
    if (open) {
      setType("service_apology");
      setAmount("");
      setDescription("");
      setEvidence([]);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const amountNum = amount === "" ? null : Number(amount);
  const isFinancial =
    FINANCIAL_RESOLUTION_TYPES.includes(type) || (amountNum != null && amountNum > 0);
  const needsAmount = FINANCIAL_RESOLUTION_TYPES.includes(type) && type !== "product_replacement";
  const amountOk = !needsAmount || (amountNum != null && amountNum > 0);
  const evidenceOk = !isFinancial || evidence.length > 0;
  const canSubmit = amountOk && evidenceOk && !mut.isPending && !uploading;

  async function onFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      const att = await uploadAttachment(file, "CClientComplaintAttachment", "file");
      const res = await uploadComplaintAttachment(complaintId, {
        fileId: att.id,
        attachmentType: "photo_evidence",
      });
      setEvidence((prev) => [...prev, { attachmentId: res.attachmentId, fileName: res.fileName }]);
    } catch (e) {
      setError((e as Error)?.message ?? "Не вдалося завантажити доказ");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync({
        resolutionType: type,
        amount: amountNum,
        currency: "UAH",
        description: description.trim() || undefined,
        evidenceAttachmentIds: evidence.map((e) => e.attachmentId),
      });
      onDone?.();
      onClose();
    } catch (e) {
      setError((e as Error)?.message ?? "Не вдалося запропонувати рішення");
    }
  }

  return (
    <div className="fixed inset-0 z-[55] flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full overflow-y-auto bg-background shadow-2xl outline-none sm:w-[480px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="text-base font-semibold">
            Запропонувати рішення
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1 block text-xs font-medium">Тип рішення</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ComplaintResolutionType)}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
            >
              {RESOLUTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {RESOLUTION_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </div>

          {isFinancial && (
            <div className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[13px] text-warning-foreground">
              <Lock className="mr-1 inline h-3.5 w-3.5" aria-hidden />
              Фінансове рішення: потребує доказів і погодження супервайзера. Докази блокуються (SHA-256) після пропозиції.
            </div>
          )}

          {(needsAmount || isFinancial) && (
            <div>
              <label className="mb-1 block text-xs font-medium">
                Сума, UAH {needsAmount && <span className="text-destructive">*</span>}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-10 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium">Опис рішення</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {isFinancial && (
            <div>
              <label className="mb-1 block text-xs font-medium">
                Докази <span className="text-destructive">*</span>
              </label>
              <ul className="space-y-1">
                {evidence.map((ev) => (
                  <li
                    key={ev.attachmentId}
                    className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5 text-sm"
                  >
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    <span className="flex-1 truncate">{ev.fileName}</span>
                    <button
                      type="button"
                      onClick={() => setEvidence((p) => p.filter((x) => x.attachmentId !== ev.attachmentId))}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label="Прибрати"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 gap-1.5"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Додати доказ
              </Button>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.tiff,.doc,.docx"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                }}
              />
            </div>
          )}

          {error && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" /> {error}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 border-t bg-background px-5 py-4">
          <Button onClick={submit} disabled={!canSubmit} className={cn("h-11 w-full text-sm font-semibold")}>
            {mut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Збереження…
              </>
            ) : isFinancial ? (
              "Подати на погодження"
            ) : (
              "Застосувати рішення"
            )}
          </Button>
          <p className="mt-1.5 text-center text-xs text-muted-foreground">
            {isFinancial
              ? "Фінансове рішення → погодження супервайзера."
              : "Нефінансове рішення застосовується одразу."}
          </p>
        </div>
      </div>
    </div>
  );
}
